import { useContext, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Breadcrumb, Entry, FlexSpace, Icon, ListChooser, Page, Pill, Row } from '../../components';
import { AuthContext, SettingsContext, ToastContext } from '../../context';
import { ActiveListContext } from '../../context/ActiveListContext';
import { SCHOOLS, SPELLS } from '../../data';
import { useWideMode } from '../../hooks';
import { classNames, subclassNames, componentsDisplay, durationDisplay, getSpellId, isConc, levelDisplay, materialComponent, rangeDisplay, timeDisplay, removeMacros } from '../../util/spell';
import './SpellDisplayPage.scss';
import { Button } from '@mui/material';

export const SpellDisplayPage = () => {
    const isWide = useWideMode();
    const { toast } = useContext(ToastContext);
    const { activeList, addSpell, hasSpell, removeSpell } = useContext(ActiveListContext);
    const { includeSubclasses } = useContext(SettingsContext);
    const params = useParams();
    const [ searchParams ] = useSearchParams();
    const [ spell, setSpell ] = useState(null);
    const [ listChooserOpen, setListChooserOpen ] = useState(false);
    const { isLoggedIn } = useContext(AuthContext);

    const spellId = isWide ? searchParams.get('spellId') : params.spellId;

    useEffect(() => {
      setSpell(SPELLS.find(s => getSpellId(s) === spellId));
    }, [spellId]);

    const attackTypes = {
        M: 'Melee',
        R: 'Ranged'
    };

    return (
        <Page className="SpellDisplayPage">
            <ListChooser open={listChooserOpen} onClose={() => setListChooserOpen(false)} spellId={spellId} />
            <Breadcrumb
                action={isLoggedIn &&
                    <Button 
                        variant='contained' 
                        color='secondary'
                        onClick={() => setListChooserOpen(true)}
                    >
                        <Icon name='plus' size='small' />
                        &nbsp;Add to a list
                    </Button>
                }
            />
            { !!spell ? <>
                <Row className='pageheader'>
                    <h2>{spell?.name}</h2>
                    <FlexSpace />
                    <Pill>{spell?.source} p{spell?.page}</Pill>
                    <Icon 
                        name='share-nodes'
                        onClick={() => {
                            navigator.clipboard.writeText(window.location.href);
                            toast('Link copied to clipboard!');
                        }}
                    />
                </Row>

                {activeList && (
                    hasSpell(spell) ? 
                        <Row onClick={() => removeSpell(spell)} >
                            <Pill className="active">
                                Added to list
                            </Pill>
                        </Row>
                    :
                        <Row onClick={() => addSpell(spell)} >
                            <Pill>
                                + Add to list
                            </Pill>
                        </Row>
                )}
                <Row>
                    {levelDisplay(spell?.level)}
                    &nbsp;
                    {SCHOOLS[spell?.school]}
                    {spell?.meta?.ritual && <Pill>ritual</Pill>}
                </Row>
                <hr />
                <Row>{classNames(spell?.classes, false).map(n => <Pill key={n}>{n}</Pill>)}</Row>
                <hr />
                {(includeSubclasses && !!subclassNames(spell?.classes).length) && <>
                    <Row>{subclassNames(spell?.classes).map(n => <Pill key={n}>{n}</Pill>)}</Row>
                    <hr />
                </>}
                <Row>
                    {(spell?.savingThrow || []).map(n => <Pill key={n}>{n} save</Pill>)}
                    {(spell?.damageInflict || []).map(n => <Pill key={n}>{n} damage</Pill>)}
                    {(spell?.spellAttack || []).map(n => 
                        <Pill key={n}>{attackTypes[n]} spell attack</Pill>
                    )}
                    {(spell?.conditionInflict || []).map(n => 
                        <Pill key={n}>{n} condition</Pill>
                    )}
                </Row>
                { (spell?.savingThrow || spell?.damageInflict || spell?.spellAttack) && <hr />}
                <Row>Casting time: {removeMacros(timeDisplay(spell?.time))}</Row>
                <Row>Range: {rangeDisplay(spell?.range)}</Row>
                <Row>Components: {componentsDisplay(spell?.components)}</Row>
                <Row>Duration: {isConc(spell) && 'Concentration, Up to '} {durationDisplay(spell?.duration)}</Row>
                { spell?.components?.m && 
                    <Row>*{materialComponent(spell?.components)}</Row>
                }
                <hr />
                {spell?.entries.map((e, i) => <Entry key={i} entry={e} />)}
                {!!spell?.entriesHigherLevel && (spell.entriesHigherLevel).map((e, i) => <Entry key={i} entry={e} />)}
            </> :
                !isWide && <Row>Spell not found</Row>
            }
        </Page>
    );
};